import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { useTier } from '../context/TierContext';
import { ScoreCard, SeriesBars, LoadingState, ErrorState } from '../components/primitives';
import RangeControl, { DEFAULT_RANGE, rangeLabel } from '../components/RangeControl';
import BillingSummary from '../components/BillingSummary';
import ClientUsersPanel from '../components/ClientUsersPanel';
import WelcomeEmailPanel from '../components/WelcomeEmailPanel';
import KbContentPanel from '../components/KbContentPanel';
import ClientActionsPanel from '../components/ClientActionsPanel';
import InitialKnowledgeDraftPanel from '../components/InitialKnowledgeDraftPanel';
import Modal from '../components/Modal';
import PageTabs from '../components/PageTabs';
import SeatCount from '../components/SeatCount';
import { codeOf, fmtInt, fmtMoney, timeAgo } from '../lib/format';
import { flagTone, statusTone } from '../lib/flags';
import { countActiveReps } from '../lib/selectors';
import { TimeRange } from '../types/superadmin';

enum ClientTab {
  Details = 'details',
  KnowledgeBase = 'kb'
}

// Details first: it is the default landing tab.
const CLIENT_TABS = [
  { key: ClientTab.Details, label: 'Details' },
  { key: ClientTab.KnowledgeBase, label: 'Knowledge Base' }
] as const;

// The tab lives in the URL (?tab=kb) so a KB review can be linked directly.
const TAB_PARAM = 'tab';

const parseTab = (value: string | null): ClientTab =>
  value === ClientTab.KnowledgeBase ? ClientTab.KnowledgeBase : ClientTab.Details;

export default function ClientDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [range, setRange] = useState<TimeRange>(DEFAULT_RANGE);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTab(searchParams.get(TAB_PARAM));
  const [showWelcomeEmail, setShowWelcomeEmail] = useState(false);
  const { tier, setTier } = useTier();
  // Current state (header, billing, tickets, flags) and range activity (cards,
  // charts, rep stats) load separately; a new range refetches only activity.
  const { data, loading, error } = useApiData(() => api.getClient(tenantId as string), [tenantId]);
  const activityState = useApiData(
    () => api.getClientActivity(tenantId as string, range),
    [tenantId, JSON.stringify(range)]
  );

  const c = data?.data?.client;
  const act = activityState.data?.data;

  // A deep link can land on a client from the other tier; follow it so the
  // top-nav switch always describes the page being shown.
  const clientTier = c?.tier;
  useEffect(() => {
    if (clientTier && clientTier !== tier) setTier(clientTier);
  }, [clientTier, tier, setTier]);

  const selectTab = (tab: ClientTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === ClientTab.Details) next.delete(TAB_PARAM);
    else next.set(TAB_PARAM, tab);
    setSearchParams(next, { replace: true });
  };

  if (loading) return <LoadingState />;
  if (error || !c) return <ErrorState message={error || 'Client not found.'} />;

  const reps = act?.reps ?? [];
  const series = act?.series ?? [];
  // "Active" for the selected period = reps with any call/training activity.
  const activeInPeriod = countActiveReps(reps);
  const inactiveInPeriod = Math.max(0, c.users.total - activeInPeriod);
  // Range cards show a dash until the activity half arrives.
  const rangeInt = (value: number | undefined) => (value === undefined ? '—' : fmtInt(value));
  const scoreValue = (value: number | null | undefined) => (value == null ? (act ? 'Not available' : '—') : value);
  // Current-state flags (billing, KB, seats, inactive users) + performance
  // flags recomputed for the selected range.
  const flags = [...c.atRisk.flags.filter((f) => !f?.isPerformance), ...(act?.performanceFlags ?? [])];
  const sub = c.billing.subscription;
  // A trial client's Stripe subscription is canceled on purpose; its plan is "trial".
  const status = sub?.trialAccessEnabled ? 'trial' : sub?.status || 'none';
  const heroTone = c.atRisk.isAtRisk ? 'neg' : statusTone(status);
  const statusLabel = c.atRisk.isAtRisk ? 'At risk' : status;

  return (
    <div>
      {/* Breadcrumb + range */}
      <div className="filter-bar">
        <Link className="dd" to="/clients">
          ← All clients
        </Link>
        <span className="sep" />
        <span className="label">Client</span>
        <span className="dd-pill">{c.displayName}</span>
        <span className="grow" />
        <RangeControl value={range} onChange={setRange} />
      </div>

      {/* Client hero */}
      <div className="client-hero">
        <div>
          <div className="eyebrow">Clients · drilldown</div>
          <div className="client-id">
            <span className="logo">{codeOf(c.tenantId)}</span>
            <div>
              <h1 className="title" style={{ fontSize: 44, margin: '0 0 4px' }}>
                <span className="serif">{c.displayName}</span>
              </h1>
              <div className="meta-row">
                <span>{c.tenantId}</span>
                <span style={{ color: 'var(--ink-disabled)' }}>·</span>
                <span>{status}</span>
                <span style={{ color: 'var(--ink-disabled)' }}>·</span>
                <SeatCount
                  used={sub?.usedUserSeats}
                  total={sub?.totalUserSeats}
                  isTrial={sub?.trialAccessEnabled === true}
                  suffix=" seats"
                />
                <span style={{ color: 'var(--ink-disabled)' }}>·</span>
                <span>{fmtMoney(c.billing.revenue.lifetime)} lifetime</span>
              </div>
            </div>
          </div>
          <p className="subtitle">
            Last session {timeAgo(c.lastSessionAt)}. {c.users.active} of {c.users.total} accounts enabled.
            Activity below covers <b>{rangeLabel(range)}</b>.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <span className={'status-pill ' + heroTone}>
            <span className="dot" />
            {statusLabel}
          </span>
          {c.frontendUrl && (
            <a className="btn" href={c.frontendUrl} target="_blank" rel="noreferrer">
              Open client workspace
            </a>
          )}
          <button type="button" className="btn" onClick={() => setShowWelcomeEmail(true)}>
            Send welcome email
          </button>
        </div>
      </div>

      {/*
        Scope scorecard — all range-scoped. Calls adherence + training are the
        SAME numbers the reps table below pools to (and the flags use), so the
        card, the reps aggregate, and "Why flagged" always match.
      */}
      <div className="scorecard seven">
        <ScoreCard label="Total users" value={fmtInt(c.users.total)} sub={act ? `${activeInPeriod} active, ${inactiveInPeriod} inactive, ${rangeLabel(range)}` : rangeLabel(range)} />
        <ScoreCard label="Sessions" value={rangeInt(act?.activity?.sessions)} sub={rangeLabel(range)} />
        <ScoreCard label="Call minutes" value={rangeInt(act?.activity?.minutes)} sub={rangeLabel(range)} />
        <ScoreCard label="Call quality" value={scoreValue(act?.aggregate?.callQualityScore)} unit={act?.aggregate?.callQualityScore != null ? '%' : undefined} sub={`Handling + playbook, ${rangeLabel(range)}`} />
        <ScoreCard label="Training quality" value={scoreValue(act?.aggregate?.trainingQualityScore)} unit={act?.aggregate?.trainingQualityScore != null ? '%' : undefined} sub={`Training quality score, ${rangeLabel(range)}`} />
        <ScoreCard label="Training completion" value={scoreValue(act?.aggregate?.trainingCompletion)} unit={act?.aggregate?.trainingCompletion != null ? '%' : undefined} sub={`Abandoned sessions count as 0%, ${rangeLabel(range)}`} />
        <ScoreCard label="Open tickets" value={fmtInt(c.support.openTickets)} sub={`${c.support.unreadFeedback} unread feedback`} />
      </div>

      {activityState.error && <ErrorState message={activityState.error} />}

      <PageTabs tabs={CLIENT_TABS} active={activeTab} onChange={selectTab} label="Client sections" />

      {activeTab === ClientTab.Details && (
        <div role="tabpanel" id={`tabpanel-${ClientTab.Details}`} aria-labelledby={`tab-${ClientTab.Details}`}>
          {/* Billing — trial vs subscription, list price vs actually paid */}
          <BillingSummary billing={c.billing} />

          {/* At-risk flags (calls/training follow the range; rest are current-state) */}
          {flags.length > 0 && (
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>Why this client is flagged</h3>
                  <p className="desc">
                    Billing, KB, seat and inactive-user signals are current; calls, training and call quality
                    reflect {rangeLabel(range)}.
                  </p>
                </div>
              </div>
              <div className="flags">
                {flags.map((f) => (
                  <span key={f.key} className={'chip ' + flagTone(f.key)} title={f.label}>
                    {f.detail || f.label}
                  </span>
                ))}
              </div>
            </section>
          )}


          {/* Users — live roster joined to each person's range stats; rows expand to actions + charts */}
          <ClientUsersPanel
            tenantId={tenantId as string}
            clientName={c.displayName}
            reps={reps}
            range={range}
          />

          {/* By-day activity charts (range) */}
          <section className="section">
            <div className="split">
              <div>
                <div className="section-head">
                  <div>
                    <h3>Minutes by day</h3>
                    <p className="desc">Call minutes, {rangeLabel(range)}.</p>
                  </div>
                </div>
                <SeriesBars data={series} field="minutes" unit=" min" />
              </div>
              <div>
                <div className="section-head">
                  <div>
                    <h3>Sessions by day</h3>
                    <p className="desc">Call sessions, {rangeLabel(range)}.</p>
                  </div>
                </div>
                <SeriesBars data={series} field="sessions" />
              </div>
            </div>
          </section>

          {/* Open tickets (current) */}
          {c.support.openTicketList.length > 0 && (
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>Open support tickets</h3>
                  <p className="desc">Oldest first.</p>
                </div>
              </div>
              <div className="tbl">
                <table>
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th style={{ width: 120 }}>Severity</th>
                      <th style={{ width: 160 }}>Requester</th>
                      <th style={{ width: 140 }}>Opened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.support.openTicketList.map((ticket) => (
                      <tr key={ticket.ticketId}>
                        <td>{ticket.title}</td>
                        <td>
                          <span className={'chip ' + (ticket.severity === 'high' ? 'neg' : ticket.severity === 'medium' ? 'warn' : '')}>
                            {ticket.severity}
                          </span>
                        </td>
                        <td className="mono" style={{ color: 'var(--ink-muted)' }}>
                          {ticket.name || ticket.email || 'Not available'}
                        </td>
                        <td>{timeAgo(ticket.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      {activeTab === ClientTab.KnowledgeBase && (
        <div role="tabpanel" id={`tabpanel-${ClientTab.KnowledgeBase}`} aria-labelledby={`tab-${ClientTab.KnowledgeBase}`}>
          <InitialKnowledgeDraftPanel tenantId={tenantId as string} clientName={c.displayName} />

          {/* Knowledge base readiness — live reviewer checks + manual review-agent run. */}
          <div id="client-kb-content"><KbContentPanel tenantId={tenantId as string} /></div>

          <div id="client-actions"><ClientActionsPanel tenantId={tenantId as string} /></div>
        </div>
      )}

      {showWelcomeEmail && (
        <Modal label="Welcome email" onClose={() => setShowWelcomeEmail(false)}>
          {/* Manual trigger with a QA test-send */}
          <WelcomeEmailPanel tenantId={tenantId as string} clientName={c.displayName} />
        </Modal>
      )}
    </div>
  );
}
